// React
import { useState, useEffect, ChangeEvent } from "react"

// Translations
import { useLang } from "@/context/useLang"
import { useTrans, Trans } from "@/lib/i18n"

// UI
import { cn } from "@/lib/utils"
import { Heading } from "@/components/Typography"
import { CardBody, CardWrapper, Card } from "@/components/Card"
import { Label } from "@/components/form-controls/Label"
import { useToasts } from "@/context/useToasts"
import { Disclaimer } from "@/components/Disclaimer"
import { ConfirmDialog } from "@/components/dialogs/ConfirmDialog"
import { Button } from "@/components/Button"

// Graphql
import {
	CoreInvestmentTestCategoryChoices,
	useInvestorTestMutationsInputMutation,
} from "@/api/graphql"

// DateTime
import { DateTime } from "@/lib/dates"
import { dateFormat } from "@/constants/constants"

// Forms
import { FormikProvider, useFormik } from "@/lib/formik"
import { Yup } from "@/lib/forms"
import {
	FormikSubmitButton,
	FormikInput,
} from "@/components/form-controls/formik"

// Icons
import { CheckIcon } from "@/lib/icons"

// Context
import { useInvestmentTests } from "@/context/investmentTests"
import { useCurrentUserId } from "@/context/user"

// Tracking
import { SentryReplayPrivacyClassNames } from "@/lib/sentry"
import { enableGoogleTagManager, enablePlausible } from "@/lib/analytics"

/**
 * Calculate total net worth
 * @param totalNormalIncome
 * @param totalExtraIncome
 * @param totalLiquidAssets
 * @param totalYearlyExpenses
 * @returns
 */
function calculateTotalNetWorth(
	totalNormalIncome: number,
	totalExtraIncome: number,
	totalLiquidAssets: number,
	totalYearlyExpenses: number,
) {
	return (
		Number(totalNormalIncome) +
		Number(totalExtraIncome) +
		Number(totalLiquidAssets) -
		Number(totalYearlyExpenses)
	)
}

/**
 * Calculate total capacity for loss
 * @param totalNormalIncome
 * @param totalExtraIncome
 * @param totalLiquidAssets
 * @param totalYearlyExpenses
 */
function calculateTotalCapacityForLoss(totalNetWorth: number) {
	return (Number(totalNetWorth) / 100) * 10
}

/**
 * Converts string values to number with two decimals
 *
 * TODO: Make this work for large numbers like 10.000 as well!
 * @param amount
 * @returns
 */
function convertMoneyStringToNumberFormat(amount: string) {
	const stringWithDotsInsteadOfComma = amount.replace(",", ".")
	const parsedValue = parseFloat(stringWithDotsInsteadOfComma)
	const roundedValue = Number(parsedValue.toFixed(2))
	return isNaN(roundedValue) ? "" : roundedValue
}

/**
 * Risk Calculator
 *
 * TODO: Unit test these calculations and make sure they dont give any weird negative values!
 * @returns
 */
export const InvestorRisk = () => {
	// Context
	const { investorTestRisk, refetch } = useInvestmentTests()
	const { id: userId } = useCurrentUserId()

	// Translations
	const t = useTrans("investor")
	const { formatCurrency } = useLang()

	// When visiting this page, disable all trackers (GTM + Plausible)
	useEffect(() => {
		enableGoogleTagManager(false)
		enablePlausible(false)

		// When unmounting, enable tracking again
		return () => {
			enableGoogleTagManager(true)
			enablePlausible(true)
		}
	}, [])

	// Internal state
	const [totalNetWorth, setTotalNetWorth] = useState<number>()
	const [totalCapacityForLoss, setTotalCapacityForLoss] = useState<number>()

	// Router
	const toasts = useToasts()

	// Mutation
	const createNewTestSubmission = useInvestorTestMutationsInputMutation({
		onSuccess: async (response) => {
			// do we have data? then success
			if (response?.investment_test_update?.investor_test?.id) {
				toasts.addToast({
					variant: "success",
					id: "investor-risk-success",
					text: t("investor:risk.result.success"),
				})

				// Refetch data
				void refetch()

				// Scroll to top
				window.scrollTo(0, 0)
			}
		},
	})

	// Forms
	const formCalculation = useFormik({
		initialValues: {
			totalNormalIncome: 0,
			totalExtraIncome: 0,
			totalLiquidAssets: 0,
			totalYearlyExpenses: 0,
		},
		validationSchema: Yup.object().shape({
			totalNormalIncome: Yup.string()
				.required()
				.matches(/^[0-9,.-]+$/),
			totalExtraIncome: Yup.string()
				.required()
				.matches(/^[0-9,.-]+$/),
			totalLiquidAssets: Yup.string()
				.required()
				.matches(/^[0-9,.-]+$/),
			totalYearlyExpenses: Yup.string()
				.required()
				.matches(/^[0-9,.-]+$/),
		}),
		onSubmit: async (values) => {
			const totalNetWorth = calculateTotalNetWorth(
				values.totalNormalIncome,
				values.totalExtraIncome,
				values.totalLiquidAssets,
				values.totalYearlyExpenses,
			)
			setTotalNetWorth(totalNetWorth)
			setTotalCapacityForLoss(
				calculateTotalCapacityForLoss(totalNetWorth),
			)
		},
	})

	const handleFormFieldBlur = (
		event: ChangeEvent<HTMLInputElement>,
		setFieldValue: Function,
	) => {
		const { value, name } = event.target
		setFieldValue(name, convertMoneyStringToNumberFormat(value))
	}

	return (
		<>
			<title>
				{t("common:web.title", {
					page: t("investor:risk.title"),
				})}
			</title>

			<ConfirmDialog
				className={"sentry-block" as SentryReplayPrivacyClassNames}
				isOpen={Boolean(totalCapacityForLoss)}
				onClose={() => setTotalCapacityForLoss(undefined)}
				title={t("investor:risk.result.advice-amount", {
					totalCapacityForLoss: formatCurrency(
						totalCapacityForLoss ?? 0,
					),
				})}
				description={t("investor:risk.result.advice-text", {
					totalNetWorth: formatCurrency(totalNetWorth ?? 0),
					totalCapacityForLoss: formatCurrency(
						totalCapacityForLoss ?? 0,
					),
				})}
				buttons={{
					yes: (
						<Button
							type="submit"
							onClick={(e) => {
								e.preventDefault()
								void createNewTestSubmission
									.mutateAsync({
										input: {
											investor: userId ?? "",
											category:
												CoreInvestmentTestCategoryChoices.LossCalculator,
											data: JSON.stringify({
												"total-capacity-for-loss":
													totalCapacityForLoss,
											}),
										},
									})
									.then(() => {
										setTotalCapacityForLoss(undefined)
										return
									})
							}}
						>
							{t("investor:risk.result.agree")}
						</Button>
					),
					no: <></>,
				}}
				buttonText={{
					no: "nee",
				}}
			/>

			<Disclaimer
				className="mb-3"
				title={t("investor:generic.privacy.title")}
				message={
					<Trans
						ns="investor"
						i18nKey="investor:generic.privacy.copy"
						components={{
							/* oxlint-disable jsx-a11y/anchor-has-content */
							a: (
								<a
									className="text-secondary-700 font-medium"
									href="https://www.afm.nl/nl-nl/sector/registers/vergunningenregisters/beleggingsondernemingen/details?id=70DF7EAB-C7AA-E411-A3EB-005056BE011D"
									target="_blank"
									rel="noreferrer"
								/>
								/* oxlint-enable jsx-a11y/anchor-has-content */
							),
						}}
					/>
				}
			/>

			<CardWrapper
				className={cn(
					"sentry-block" as SentryReplayPrivacyClassNames,
					"relative",
				)}
			>
				{Boolean(investorTestRisk) === true && <StepCompletedOverlay />}

				<CardBody className="w-full lg:w-2/3">
					<Heading as="h2" styleAs="h5" className="mb-3 sm:truncate">
						{t("investor:risk.heading")}
					</Heading>

					<p>{t("investor:risk.privacy")}</p>

					<div>
						<FormikProvider value={formCalculation}>
							<form onSubmit={formCalculation.handleSubmit}>
								<div className="mt-4">
									<Label>
										{t(
											"investor:risk.form.fields.regular-income.title",
										)}
									</Label>
									<p className="mb-1">
										{t(
											"investor:risk.form.fields.regular-income.subtitle",
										)}
									</p>

									<FormikInput
										name="totalNormalIncome"
										showIcon="bs-currency-euro"
										required
										className="w-full"
										onBlur={(event) =>
											handleFormFieldBlur(
												event,
												formCalculation.setFieldValue,
											)
										}
									/>
								</div>
								<div className="mt-4">
									<Label>
										{t(
											"investor:risk.form.fields.extra-income.title",
										)}
									</Label>
									<p className="mb-1">
										{t(
											"investor:risk.form.fields.extra-income.subtitle",
										)}
									</p>

									<FormikInput
										name="totalExtraIncome"
										showIcon="bs-currency-euro"
										required
										className="w-full"
										onBlur={(event) =>
											handleFormFieldBlur(
												event,
												formCalculation.setFieldValue,
											)
										}
									/>
								</div>
								<div className="mt-4">
									<Label>
										{t(
											"investor:risk.form.fields.liquid-assets.title",
										)}
									</Label>
									<p className="mb-1">
										{t(
											"investor:risk.form.fields.liquid-assets.subtitle",
										)}
									</p>

									<FormikInput
										name="totalLiquidAssets"
										showIcon="bs-currency-euro"
										required
										className="w-full"
										onBlur={(event) =>
											handleFormFieldBlur(
												event,
												formCalculation.setFieldValue,
											)
										}
									/>
								</div>
								<div className="mt-4">
									<Label>
										{t(
											"investor:risk.form.fields.expenses.title",
										)}
									</Label>
									<p className="mb-1">
										{t(
											"investor:risk.form.fields.expenses.subtitle",
										)}
									</p>

									<FormikInput
										name="totalYearlyExpenses"
										showIcon="bs-currency-euro"
										required
										className="w-full"
										onBlur={(event) =>
											handleFormFieldBlur(
												event,
												formCalculation.setFieldValue,
											)
										}
									/>
								</div>

								<FormikSubmitButton className="mt-4">
									{t("investor:risk.form.buttons.calculate")}
								</FormikSubmitButton>
							</form>
						</FormikProvider>
					</div>
				</CardBody>
			</CardWrapper>
		</>
	)
}

/**
 * StepCompletedOverlay
 * @param param0
 * @returns
 */
const StepCompletedOverlay = () => {
	// Contants
	const VALIDITY_MONTHS = 12

	// i18n
	const t = useTrans("investor")
	const { formatCurrency, formatDate } = useLang()

	// Context
	const { investorTestRisk } = useInvestmentTests()

	// Parse JSON data
	const totalCapacityForLoss = JSON.parse(investorTestRisk?.data)[
		"total-capacity-for-loss"
	]

	// Template
	return (
		<div className="absolute inset-0 z-10 bg-gray-600/50 p-5 text-center text-lg">
			<Card className="mx-auto max-w-md">
				<div className="space-y-4 text-center">
					<Heading
						as="h2"
						styleAs="h5"
						className="flex justify-center gap-2"
					>
						<div className="flex items-center">
							<CheckIcon
								aria-hidden="true"
								className="size-5 text-lg text-green-500"
							/>
						</div>
						{`Voltooid op ${DateTime.fromISO(
							investorTestRisk?.created_at,
						).toFormat(dateFormat)}`}
					</Heading>

					{/** If we have this value */}
					{totalCapacityForLoss && (
						<p className="text-gray-500">
							{`Uw adviesbedrag is: ${formatCurrency(
								totalCapacityForLoss,
							)}`}
						</p>
					)}

					{/** Show remaining time until expiry date */}
					<p>
						{t("investor:profile.generic.expiry-date", {
							validity: VALIDITY_MONTHS,
							date: formatDate(
								DateTime.fromISO(investorTestRisk?.expiry_date),
								{
									variant: "verbose",
								},
							),
						})}
					</p>
				</div>
			</Card>
		</div>
	)
}
